import type { ExistingAccountCredentials } from './components/RegisterPopupForm';
import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import AgeRestrictionBadge from '@/components/shared/AgeRestrictionBadge';
import createModalWrapper from '@/modals/components/createModalWrapper';
import Modal from '@/modals/components/Modal';
import { modalMotionPresets } from '@/modals/components/Modal/modalMotion';
import { cn } from '@/utils/cn';
import { useCloseAuthModal } from '../hooks/useCloseAuthModal';
import CompleteRegistration from './components/CompleteRegistration';
import { ExistingAccountStep } from './components/ExistingAccountStep';
import { RegisterPopupForm } from './components/RegisterPopupForm';
import { RegisterStep } from './utils/type';

interface RegisterModalProps {
  onCloseAction: () => Promise<void>;
  initialStep?: RegisterStep;
  initialEmail?: string;
}

const RegisterModal = createModalWrapper<RegisterModalProps>(({
  onCloseAction,
  initialStep = RegisterStep.REGISTER,
  initialEmail = '',
}) => {
  const t = useTranslations('register');
  const tExistingAccount = useTranslations('existing-account-modal');
  const { handleCloseModal } = useCloseAuthModal({ page: '/game/', onCloseAction });

  const [email, setEmail] = useState<string>(initialEmail);
  const [stepOfRegistration, setStepOfRegistration] = useState<RegisterStep>(initialStep);
  const [existingAccount, setExistingAccount] = useState<ExistingAccountCredentials | null>(null);
  const [isCompleteRegistrationMounted, setIsCompleteRegistrationMounted] = useState(initialStep === RegisterStep.COMPLETE_REGISTRATION);

  const isCompleteRegistration = stepOfRegistration === RegisterStep.COMPLETE_REGISTRATION;
  const isExistingAccount = stepOfRegistration === RegisterStep.EXISTING_ACCOUNT && existingAccount !== null;

  const headerTitle = useMemo(() => {
    if (isCompleteRegistration || isExistingAccount) {
      return '';
    }
    return t('title');
  }, [isCompleteRegistration, isExistingAccount, t]);

  const getAccessibleTitle = () => {
    if (isCompleteRegistration) {
      return t('request-sent');
    }
    if (isExistingAccount) {
      return tExistingAccount('title');
    }
    return undefined;
  };

  const handleExistingAccountAction = (credentials: ExistingAccountCredentials) => {
    setExistingAccount(credentials);
    setStepOfRegistration(RegisterStep.EXISTING_ACCOUNT);
  };

  const isGradientVariant = stepOfRegistration === RegisterStep.REGISTER || stepOfRegistration === RegisterStep.COMPLETE_REGISTRATION;

  return (
    <Modal
      headerTitle={headerTitle}
      accessibleTitle={getAccessibleTitle()}
      onCloseAction={handleCloseModal}
      headerClassName={isExistingAccount ? 'absolute right-0 top-0' : undefined}
      bodyClassName={isExistingAccount ? 'p-0!' : undefined}
      contentClassName={cn('lg:w-[23.75rem] lg:h-[44.6875rem] lg:rounded-3xl hide-scrollbar overflow-visible', (isCompleteRegistration || isExistingAccount) ? 'overflow-hidden' : '')}
      variant={isGradientVariant && !isExistingAccount ? 'gradient' : 'default'}
      motionPreset={modalMotionPresets.none}
      headerChild={stepOfRegistration === RegisterStep.REGISTER ? <AgeRestrictionBadge /> : undefined}
    >
      {() => (
        <>
          {isCompleteRegistrationMounted && (
            <CompleteRegistration
              email={email}
              isActive={isCompleteRegistration}
              onReturnToRegisterAction={() => {
                setIsCompleteRegistrationMounted(false);
                setStepOfRegistration(RegisterStep.REGISTER);
              }}
            />
          )}

          {isExistingAccount && existingAccount && (
            <ExistingAccountStep
              username={existingAccount.username}
              password={existingAccount.password}
              onCloseAction={onCloseAction}
            />
          )}

          {!isCompleteRegistration && !isExistingAccount && (
            <RegisterPopupForm
              setEmail={setEmail}
              setStepOfRegistration={setStepOfRegistration}
              onExistingAccountAction={handleExistingAccountAction}
              onRegistrationFailureAction={() => setIsCompleteRegistrationMounted(false)}
              onRegistrationStartAction={() => setIsCompleteRegistrationMounted(true)}
            />
          )}
        </>
      )}
    </Modal>
  );
});

export default RegisterModal;
